import { useState, useCallback } from 'react';
import { Shirt, Armchair, MessageSquareText, Ban, Smile, Video, ShieldCheck, ChevronRight, AlertTriangle } from 'lucide-react';
import useInterviewStore from '../../stores/interviewStore';
import WebcamPanel from './WebcamPanel';

const INSTRUCTIONS = [
  { icon: Shirt, text: 'Wear proper attire, as you would for a real interview.' },
  { icon: Armchair, text: 'Sit with good posture in a well-lit, quiet space.' },
  { icon: MessageSquareText, text: 'Answer exactly as you would in a real interview — treat it seriously.' },
  { icon: Ban, text: "Avoid unwanted or casual conversation once the interview begins." },
  { icon: Smile, text: 'Be open, stay relaxed, and enjoy the process.' },
];

/**
 * PreInterviewGate — blocks the interview from starting until the student
 * reads the instructions, consents to being monitored on camera, and
 * grants camera access. Nothing in InterviewRoom (including the AI's
 * opening message) fires until this gate passes.
 */
export default function PreInterviewGate({ studentName, onStart }) {
  const { cameraOn, setCameraOn } = useInterviewStore();
  const [agreed, setAgreed] = useState(false);
  const [camPermission, setCamPermission] = useState(null); // null=not requested yet, true/false after

  const handleEnableCamera = useCallback(() => {
    if (!agreed) return;
    setCameraOn(true);
  }, [agreed, setCameraOn]);

  const handlePermissionChange = useCallback((granted) => {
    setCamPermission(granted);
  }, []);

  const canStart = agreed && cameraOn && camPermission === true;

  return (
    <div
      className="dark min-h-screen flex items-center justify-center p-4"
      style={{ background: '#0B0A14' }}
      data-testid="pre-interview-gate"
    >
      <div className="w-full max-w-2xl bg-[#14161B] border border-[#272B36] rounded-2xl p-6 md:p-8 space-y-6">
        <div>
          <p className="text-[10px] font-heading uppercase tracking-[0.2em] text-[#FFC233] font-semibold mb-1">
            Before You Begin
          </p>
          <h1 className="text-xl md:text-2xl font-bold font-heading text-white">
            Ready for your interview, {studentName || 'there'}?
          </h1>
        </div>

        {/* Instructions */}
        <div className="space-y-2.5" data-testid="interview-instructions">
          {INSTRUCTIONS.map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-lg bg-[#3D1F6E]/30 flex items-center justify-center shrink-0 mt-0.5">
                <item.icon className="w-3.5 h-3.5 text-[#A78BFA]" />
              </div>
              <p className="text-sm text-[#E4E4E7] leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>

        {/* Consent */}
        <div className="rounded-xl border border-[#FFC233]/25 bg-[#FFC233]/5 p-4 space-y-3">
          <div className="flex items-start gap-2.5">
            <ShieldCheck className="w-4 h-4 text-[#FFC233] shrink-0 mt-0.5" />
            <p className="text-xs text-[#E4E4E7] leading-relaxed">
              This interview requires your camera to be on for its full duration — your video feed will be
              monitored throughout the session. This is compulsory to proceed.
            </p>
          </div>
          <label className="flex items-start gap-2.5 cursor-pointer" data-testid="consent-checkbox-label">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              data-testid="consent-checkbox"
              className="mt-0.5 w-4 h-4 rounded border-[#272B36] accent-[#FFC233] shrink-0"
            />
            <span className="text-xs text-[#9BA1B0]">
              I have read the instructions above and I consent to my camera feed being monitored during this
              interview.
            </span>
          </label>
        </div>

        {/* Camera preview / enable */}
        {!cameraOn ? (
          <button
            onClick={handleEnableCamera}
            disabled={!agreed}
            data-testid="enable-camera-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold font-heading btn-cta disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Video className="w-4 h-4" />
            Enable Camera & Consent
          </button>
        ) : (
          <div className="space-y-3">
            <div className="h-48 rounded-xl overflow-hidden">
              <WebcamPanel studentName={studentName} cameraOn={cameraOn} onToggleCamera={() => {}} onPermissionChange={handlePermissionChange} />
            </div>
            {camPermission === false && (
              <div className="flex items-start gap-2.5 rounded-lg border border-red-500/30 bg-red-500/10 p-3">
                <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                <p className="text-xs text-red-300">
                  Camera access is required to proceed. Please allow camera access in your browser's site
                  settings, then try again.
                </p>
              </div>
            )}
            {camPermission === false && (
              <button
                onClick={handleEnableCamera}
                data-testid="retry-camera-btn"
                className="w-full px-4 py-2.5 rounded-lg text-sm font-semibold font-heading bg-[#1C1F26] text-white hover:bg-[#272B36] transition-colors"
              >
                Try Again
              </button>
            )}
          </div>
        )}

        <button
          onClick={onStart}
          disabled={!canStart}
          data-testid="start-interview-gate-btn"
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold font-heading btn-cta disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Start Interview
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
